import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { ProjectModelControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import {
  selectProjectModelsList,
  findProjectModelByName,
} from '@rapider/project-data';

/**
 * @class NavigateToProjectModelComponent
 * @description A routing resolution component that accepts a semantic `projectModelName`.
 * It evaluates the NgRx cache first, or queries the database fallback to locate the true Model ID, and then seamlessly redirects.
 */
@Component({
  selector: 'rapider-navigate-to-project-model',
  standalone: true,
  template: `
    <div
      class="w-full h-full flex items-center justify-center min-h-[500px] bg-body-background"
    >
      <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
    </div>
  `,
  host: { class: 'block w-full h-full' },
})
export class NavigateToProjectModelComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly projectModelController = inject(
    ProjectModelControllerService,
  );
  private readonly notificationService = inject(NotificationService);

  ngOnInit() {
    const modelName =
      this.route.snapshot.paramMap.get('projectModelName')?.toLowerCase() || '';
    if (!modelName) {
      this.router.navigate(['/database']);
      return;
    }

    const storedModels = this.store.selectSignal(selectProjectModelsList)();

    if (storedModels && storedModels.length > 0) {
      // Search the cached NgRx store using strict lowercased normalization mappings
      const found = findProjectModelByName(storedModels, modelName);

      if (found) {
        this.router.navigate(['/database', found.id]);
      } else {
        this.notificationService.createNotification(
          'warning',
          'Not Found',
          `Database model "${modelName}" not found.`,
        );
        this.router.navigate(['/database']);
      }
    } else {
      // Deep fallback: If a user refreshed the page losing the cached context, fetch a lightweight list from backend directly
      // Do not rely on the NGRX here to have the project models loaded, as this component's purpose is to resolve that context in the first place, we will fetch a minimal list of models with only id, name and dataTableName fields to find the correct model and navigate to it, we will not save this list to the store to avoid overwriting any existing cached data and also to keep it lightweight, we will directly query the API and find the matching model from the response, if found we navigate to it, if not we show a notification and navigate back to the database list.
      const fallbackFilter = JSON.stringify({
        fields: { id: true, name: true, dataTableName: true },
      });
      this.projectModelController.find({ filter: fallbackFilter }).subscribe({
        next: (res: any[]) => {
          const found = findProjectModelByName(res, modelName);

          if (found) {
            this.router.navigate(['/database', found.id]);
          } else {
            this.notificationService.createNotification(
              'warning',
              'Not Found',
              `Database model "${modelName}" not found.`,
            );
            this.router.navigate(['/database']);
          }
        },
        error: () => {
          this.notificationService.createNotification(
            'error',
            'Error',
            `Failed to query database context.`,
          );
          this.router.navigate(['/database']);
        },
      });
    }
  }
}
